import React, { useState, useEffect } from 'react';
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const PopupManagementSection = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'text', // 'text', 'image', 'both'
    imageFile: null,
    imageUrl: '',
    buttonText: 'Learn More',
    buttonLink: '',
    isActive: false
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'popups'));
      const popupData = [];
      querySnapshot.forEach((doc) => {
        popupData.push({ id: doc.id, ...doc.data() });
      });
      setPopups(popupData.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate()));
    } catch (error) {
      console.error('Error fetching popups:', error);
      alert('Error fetching popups');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size should be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, imageFile: file }));
    }
  };

  const uploadImage = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('project', 'Test');
    formDataUpload.append('type', 'TestImages');
    formDataUpload.append('image', file);

    try {
      const response = await fetch('https://imageapi.garanipublication.com/upload.php', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result.success && result.url) {
        return result.url;
      } else {
        throw new Error('Upload failed - no URL in response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const deleteImage = async (imageUrl) => {
    try {
      const response = await fetch('https://imageapi.garanipublication.com/delete.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `url=${encodeURIComponent(imageUrl)}`,
      });

      if (!response.ok) {
        console.warn('Failed to delete image from server');
      } else {
        const result = await response.json();
        if (result.success) {
          console.log('Image deleted successfully from server');
        } else {
          console.warn('Image deletion response:', result.message || 'Unknown error');
        }
      }
    } catch (error) {
      console.warn('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Validation based on popup type
      if (formData.type === 'text' && (!formData.title.trim() || !formData.description.trim())) {
        alert('Title and description are required for text-only popups');
        setUploading(false);
        return;
      }
      
      if (formData.type === 'image' && !formData.imageFile && !formData.imageUrl) {
        alert('Image is required for image-only popups');
        setUploading(false);
        return;
      }
      
      if (formData.type === 'both' && (!formData.title.trim() || !formData.description.trim()) && !formData.imageFile && !formData.imageUrl) {
        alert('Either text content (title and description) or image is required for combined popups');
        setUploading(false);
        return;
      }

      let imageUrl = formData.imageUrl;

      // Upload new image if provided
      if (formData.imageFile) {
        try {
          imageUrl = await uploadImage(formData.imageFile);
          if (!imageUrl) {
            throw new Error('Image upload failed - no URL returned');
          }
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          alert('Failed to upload image. Please try again.');
          setUploading(false);
          return;
        }
      }

      const popupData = {
        title: formData.title || '',
        description: formData.description || '',
        type: formData.type,
        imageUrl: (formData.type === 'image' || formData.type === 'both') ? (imageUrl || '') : '',
        buttonText: formData.buttonText || '',
        buttonLink: formData.buttonLink || '',
        isActive: formData.isActive,
        updatedAt: new Date()
      };

      // Remove empty string fields to avoid storing unnecessary data
      Object.keys(popupData).forEach(key => {
        if (popupData[key] === '' && key !== 'imageUrl') {
          delete popupData[key];
        }
      });

      if (editingPopup) {
        // Update existing popup
        if (editingPopup.imageUrl && editingPopup.imageUrl !== imageUrl && formData.imageFile) {
          // Delete old image if a new one was uploaded
          await deleteImage(editingPopup.imageUrl);
        }
        
        await updateDoc(doc(db, 'popups', editingPopup.id), popupData);
        alert('Popup updated successfully!');
      } else {
        // Create new popup
        popupData.createdAt = new Date();
        await addDoc(collection(db, 'popups'), popupData);
        alert('Popup created successfully!');
      }

      // Reset form and refresh list
      resetForm();
      fetchPopups();
    } catch (error) {
      console.error('Error saving popup:', error);
      alert('Error saving popup. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'text',
      imageFile: null,
      imageUrl: '',
      buttonText: 'Learn More',
      buttonLink: '',
      isActive: false
    });
    setEditingPopup(null);
    setShowForm(false);
  };

  const handleEdit = (popup) => {
    setFormData({
      title: popup.title,
      description: popup.description,
      type: popup.type,
      imageFile: null,
      imageUrl: popup.imageUrl || '',
      buttonText: popup.buttonText,
      buttonLink: popup.buttonLink,
      isActive: popup.isActive
    });
    setEditingPopup(popup);
    setShowForm(true);
  };

  const handleDelete = async (popup) => {
    if (!window.confirm('Are you sure you want to delete this popup?')) {
      return;
    }

    try {
      // Delete image from server if exists
      if (popup.imageUrl) {
        await deleteImage(popup.imageUrl);
      }

      // Delete from Firestore
      await deleteDoc(doc(db, 'popups', popup.id));
      alert('Popup deleted successfully!');
      fetchPopups();
    } catch (error) {
      console.error('Error deleting popup:', error);
      alert('Error deleting popup');
    }
  };

  const toggleStatus = async (popup) => {
    try {
      // If activating this popup, deactivate all others first
      if (!popup.isActive) {
        const activePopups = popups.filter(p => p.isActive && p.id !== popup.id);
        for (const activePopup of activePopups) {
          await updateDoc(doc(db, 'popups', activePopup.id), { isActive: false });
        }
      }

      await updateDoc(doc(db, 'popups', popup.id), { 
        isActive: !popup.isActive,
        updatedAt: new Date()
      });
      
      fetchPopups();
    } catch (error) {
      console.error('Error updating popup status:', error);
      alert('Error updating popup status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Popup Management</h2>
          <p className="text-gray-600">Manage website popups with text, images, or both</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <span>+</span>
          <span>Create Popup</span>
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingPopup ? 'Edit Popup' : 'Create New Popup'}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Popup Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Popup Type
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'text', label: 'Text Only', icon: '📝' },
                      { value: 'image', label: 'Image Only', icon: '🖼️' },
                      { value: 'both', label: 'Text + Image', icon: '🎨' }
                    ].map(type => (
                      <label key={type.value} className="cursor-pointer">
                        <input
                          type="radio"
                          name="type"
                          value={type.value}
                          checked={formData.type === type.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`border-2 rounded-lg p-4 text-center transition-all ${
                          formData.type === type.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="text-2xl mb-2">{type.icon}</div>
                          <div className="text-sm font-medium">{type.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Title (for text and both types) */}
                {(formData.type === 'text' || formData.type === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter popup title"
                      required={formData.type === 'text' || formData.type === 'both'}
                    />
                  </div>
                )}

                {/* Description (for text and both types) */}
                {(formData.type === 'text' || formData.type === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter popup description"
                    />
                  </div>
                )}

                {/* Image Upload (for image and both types) */}
                {(formData.type === 'image' || formData.type === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image {formData.type === 'image' ? '*' : ''}
                    </label>
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={formData.type === 'image' && !formData.imageUrl}
                      />
                      {formData.imageUrl && (
                        <div className="mt-2">
                          <img 
                            src={formData.imageUrl} 
                            alt="Preview" 
                            className="max-w-full h-32 object-cover rounded-md border"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Button Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    name="buttonText"
                    value={formData.buttonText}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Button text (optional)"
                  />
                </div>

                {/* Button Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Link
                  </label>
                  <input
                    type="url"
                    name="buttonLink"
                    value={formData.buttonLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com (optional)"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Make this popup active (Note: Only one popup can be active at a time)
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {uploading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    <span>{editingPopup ? 'Update' : 'Create'} Popup</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Popups List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            All Popups ({popups.length})
          </h3>
        </div>

        {popups.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No popups yet</h3>
            <p className="text-gray-600 mb-4">Create your first popup to engage with visitors</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Create Popup
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {popups.map((popup) => (
              <div key={popup.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        popup.type === 'text' ? 'bg-blue-100 text-blue-800' :
                        popup.type === 'image' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {popup.type === 'text' ? '📝 Text Only' :
                         popup.type === 'image' ? '🖼️ Image Only' :
                         '🎨 Text + Image'}
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        popup.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {popup.isActive ? '✅ Active' : '⏸️ Inactive'}
                      </div>
                    </div>

                    {popup.title && (
                      <h4 className="text-lg font-medium text-gray-900 mb-1">
                        {popup.title}
                      </h4>
                    )}

                    {popup.description && (
                      <p className="text-gray-600 mb-2 line-clamp-2">
                        {popup.description}
                      </p>
                    )}

                    {popup.imageUrl && (
                      <div className="mb-2">
                        <img 
                          src={popup.imageUrl} 
                          alt="Popup" 
                          className="h-20 w-32 object-cover rounded border"
                        />
                      </div>
                    )}

                    <div className="text-sm text-gray-500">
                      Created: {popup.createdAt?.toDate().toLocaleDateString()}
                      {popup.updatedAt && (
                        <span className="ml-4">
                          Updated: {popup.updatedAt.toDate().toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => toggleStatus(popup)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        popup.isActive
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {popup.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleEdit(popup)}
                      className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1 rounded text-sm font-medium transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(popup)}
                      className="bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1 rounded text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupManagementSection;
